import { codeVerifier, codeChallenge } from './utils/codegen';
import { getApiRegion } from './utils/apiRegion';

let env = process.env.REACT_APP_DPX_ENV;

if (env !== 'LOCAL' && env !== 'OFFLINE') {
  env = 'PRD';
}

export const appEnv = env;

/**
 * Randomly select 'eu' or 'gl' on first load, then persist the working region
 * to localStorage via persistApiRegion() after a successful API call.
 * OFFLINE always uses 'eu' as it targets localhost.
 */
export const apiRegion = env === 'OFFLINE' ? 'eu' : getApiRegion();

const environments = {
  OFFLINE: 'sit',
  LOCAL: 'sit',
  DEV: 'dev',
  SIT: 'sit',
  UAT: 'uat',
  PRD: 'prd',
};

/**
 * Environment URLs
 */
const envUrls = {
  OFFLINE: 'http://localhost',
  LOCAL: 'http://localhost',
  DEV: 'https://dev.dpx.cx.nonp.cochlear.cloud',
  SIT: 'https://portal-sit.mycochlear.com',
  UAT: 'https://portal-uat.mycochlear.com',
  PRD: 'https://portal.mycochlear.com',
};

export const envUrl = envUrls[env];

const envUrlsClinicianManagement = {
  OFFLINE: 'http://localhost:3000',
  LOCAL: 'http://localhost:3000',
  DEV: 'https://clinicadmin-dev.mycochlear.com',
  SIT: 'https://clinicadmin-sit.mycochlear.com',
  UAT: 'https://clinicadmin-uat.mycochlear.com',
  PRD: 'https://clinicadmin.mycochlear.com',
};
export const envUrlClinicianManagment =
  envUrlsClinicianManagement[env];

const envUrlsSRPortal = {
  OFFLINE: 'http://localhost:3008',
  LOCAL: 'http://localhost:3008',
  DEV: 'https://srportal-dev.cx.nonp.cochlear.cloud',
  SIT: 'https://srportal-sit.cx.nonp.cochlear.cloud',
  UAT: 'https://serviceportal-uat.mycochlear.com',
  PRD: 'https://serviceportal.mycochlear.com',
};
export const envUrlSRPortal = envUrlsSRPortal[env];
/**
 * OnboardingURL
 */
const onboardingURLs = {
  OFFLINE:
    'https://sit.accounts.ciam.nonp.cochlear.cloud/account-creation/create-own?',
  LOCAL:
    'https://sit.accounts.ciam.nonp.cochlear.cloud/account-creation/create-own?',
  DEV: 'https://sit.accounts.ciam.nonp.cochlear.cloud/account-creation/create-own?',
  SIT: 'https://sit.accounts.cochlear.com/account-creation/create-own?',
  UAT: 'https://uat.accounts.cochlear.com/account-creation/create-own?',
  PRD: 'https://accounts.cochlear.com/account-creation/create-own?',
};

export const onboardingURL = onboardingURLs[env];

/**
 * Cochlear Learning Centre (Docebo)
 */
const cochlearLearningCentreURLs = {
  OFFLINE: 'https://cochlearsandbox.docebosaas.com/learn/auth0/login',
  LOCAL: 'https://cochlearsandbox.docebosaas.com/learn/auth0/login',
  DEV: 'https://cochlearsandbox.docebosaas.com/learn/auth0/login',
  SIT: 'https://cochlearsandbox.docebosaas.com/learn/auth0/login',
  UAT: 'https://cochlearsandbox.docebosaas.com/learn/auth0/login',
  PRD: 'https://cochlear.docebosaas.com/learn/auth0/login',
};

export const cochlearLearningCentreURL =
  cochlearLearningCentreURLs[env];

/**
 * Cochlear Learning Centre (Cochlear Academy / Seertech)
 */
const cochlearLearningCentreAcademyURLs = {
  OFFLINE:
    'https://ilearningtest.seertechsolutions.com.au/auth/oauth2/authorization/cochlear-academy-uat',
  LOCAL:
    'https://ilearningtest.seertechsolutions.com.au/auth/oauth2/authorization/cochlear-academy-uat',
  DEV: 'https://ilearningtest.seertechsolutions.com.au/auth/oauth2/authorization/cochlear-academy-uat',
  SIT: 'https://ilearningtest.seertechsolutions.com.au/auth/oauth2/authorization/cochlear-academy-uat',
  UAT: 'https://ilearningtest.seertechsolutions.com.au/auth/oauth2/authorization/cochlear-academy-uat',
  PRD: 'https://ilearning.seertechsolutions.com.au/auth/oauth2/authorization/cochlear-academy',
};

export const cochlearLearningCentreAcademyURL =
  cochlearLearningCentreAcademyURLs[env];

/**
 * Order Forms URLs
 */
export const orderFormsURLs = {
  US: 'https://www.cochlear.us/OrderForms',
  AU: 'https://www.cochlear.com/au/orders',
  NZ: 'https://www.cochlear.com/nz/orders',
};

/**
 * Authorizer redirect URL
 */
const redirectUri = `${envUrls[env]}/authorize`;

/**
 * Cookie domains for domain level cookies
 */
const cookieDomains = {
  OFFLINE: '',
  LOCAL: '',
  DEV: '.cochlear.cloud',
  SIT: '.mycochlear.com',
  UAT: '.mycochlear.com',
  PRD: '.mycochlear.com',
};
export const cookieDomain = cookieDomains[env];
export const ID_TOKEN_COOKIE = 'ct_id';
export const ACCESS_TOKEN_COOKIE = 'ct_prf';
export const REFRESH_TOKEN_COOKIE = 'ctr_prf';
export const PROVIDER_DETAILS_COOKIE = 'cpd';
export const SHARED_MENU_COOKIE = 'cd_shmn';
export const USER_COOKIE = 'cd_user';
export const DPX_BOUND_COOKIE = 'dpx_bound';
export const DPX_OPTOUT_COOKIE = 'dpx_optout';

// To remember when provider token failed due to non-existant relationship.
// Upon next login, force fetch providers from API with no-cache header so that the API cache is refreshed
export const CACHE_CONTROL_COOKIE = 'cd_cc';

/**
 * MCP
 */
const mcpDomains = {
  OFFLINE: 'https://secure-sit.mycochlear.com',
  LOCAL: 'https://secure-sit.mycochlear.com',
  DEV: 'https://secure-sit.mycochlear.com',
  SIT: 'https://secure-sit.mycochlear.com',
  UAT: 'https://secure-uat.mycochlear.com',
  PRD: 'https://secure.mycochlear.com',
};

export const disableIframes = () => {
  return (
    env.toUpperCase() === 'DEV' || env.toUpperCase() === 'OFFLINE'
  );
};

/**
 * Clinician Portal
 */
const clinicianPortalDomains = {
  OFFLINE: 'https://clinician-portal-sit.mycochlear.com',
  LOCAL: 'https://clinician-portal-sit.mycochlear.com',
  DEV: 'https://clinician-portal-sit.mycochlear.com',
  SIT: 'https://clinician-portal-sit.mycochlear.com',
  UAT: 'https://clinician-portal-uat.mycochlear.com',
  PRD: 'https://clinician-portal.mycochlear.com',
};

/**
 * Experience Layer API endpoints
 */
// eslint-disable-next-line import/no-mutable-exports
let providerListEndpoint;

switch (env) {
  case 'PRD':
    providerListEndpoint = `https://api.${apiRegion}.cochlear.com/mcp/v1/providers`;
    break;

  case 'OFFLINE':
    providerListEndpoint = 'http://localhost:3000';
    break;

  default:
    providerListEndpoint = `https://${environments[env]}.api.${apiRegion}.cochlear.com/mcp/v1/providers`;
    break;
}

export { providerListEndpoint };

// eslint-disable-next-line import/no-mutable-exports
let serviceRequestEndpoint;

switch (env) {
  case 'PRD':
    serviceRequestEndpoint = `https://api.${apiRegion}.cochlear.com/mcp/v1/providers`;
    break;

  case 'OFFLINE':
    serviceRequestEndpoint = 'http://localhost:3000';
    break;

  default:
    serviceRequestEndpoint = `https://${environments[env]}.api.${apiRegion}.cochlear.com/mcp/v1/providers`;
    break;
}

export { serviceRequestEndpoint };

export const authTokenEndpoint = `${providerListEndpoint}/oauth/token`;
export const providerTokenEndpoint = `${providerListEndpoint}/oauth/provider-token`;
export const refreshTokenEndpoint = `${providerListEndpoint}/oauth/refresh-token`;

/**
 * Build the probe URL for a given region ('eu' or 'gl').
 * Used by the pre-auth reachability check in Authorizer.
 * For OFFLINE the endpoint is localhost, so probing is skipped by
 * callers that check the env first.
 *
 * @param {'eu'|'gl'} region
 * @returns {string}
 */
export function buildRegionProbeUrl(region) {
  if (env === 'PRD') {
    return `https://api.${region}.cochlear.com`;
  }
  if (env === 'OFFLINE') {
    return 'http://localhost:3000';
  }
  return `https://${environments[env]}.api.${region}.cochlear.com`;
}

/**
 * Auth0 Config
 */
const auth0Domains = {
  OFFLINE: 'https://sit.id.cochlear.com',
  LOCAL: 'https://sit.id.cochlear.com',
  DEV: 'https://sit.id.cochlear.com',
  SIT: 'https://sit.id.cochlear.com',
  UAT: 'https://uat.id.cochlear.com',
  PRD: 'https://id.cochlear.com',
};

const auth0ClientIds = {
  OFFLINE: 'YN6EuxNo4Hqpe4mJKOiNp8n5yJrFqZCY',
  LOCAL: 'YN6EuxNo4Hqpe4mJKOiNp8n5yJrFqZCY',
  DEV: 'YN6EuxNo4Hqpe4mJKOiNp8n5yJrFqZCY',
  SIT: 'YN6EuxNo4Hqpe4mJKOiNp8n5yJrFqZCY',
  UAT: '4Hdm5KnExs8B1BsaKiiUPgpvZN4xOY75',
  PRD: 'A4X2Cj5uK72Shqy5An3eBDrFqUu1oE8k',
};
const auth0ClientId = auth0ClientIds[env];

const auth0CXCAudiences = {
  OFFLINE: 'https://sit.api.cochlear.com/cxc',
  LOCAL: 'https://sit.api.cochlear.com/cxc',
  DEV: 'https://sit.api.cochlear.com/cxc',
  SIT: 'https://sit.api.cochlear.com/cxc',
  UAT: 'https://uat.api.cochlear.com/cxc',
  PRD: 'https://api.cochlear.com/cxc',
};
const auth0CXCAudience = auth0CXCAudiences[env];

const virtualSoftwareIds = {
  OFFLINE: 'g4n2Xhokll4Je4I4iuCpj33qibn6k4Nl',
  LOCAL: 'g4n2Xhokll4Je4I4iuCpj33qibn6k4Nl',
  DEV: 'g4n2Xhokll4Je4I4iuCpj33qibn6k4Nl',
  SIT: 'g4n2Xhokll4Je4I4iuCpj33qibn6k4Nl',
  UAT: 'CaBFcC76gUlSjqHTrXSsu8YMjJxy9rEL',
  PRD: 'hfeX70FMjPmI98iiL4ZgZkdDVzdCGlR5',
};

const virtualSoftwareId = virtualSoftwareIds[env];

const virtualSoftwareClientIds = {
  OFFLINE: '3D068760682950',
  LOCAL: '3D068760682950',
  DEV: '3D068760682950',
  SIT: '3D068760682950',
  UAT: '3D068760682950',
  PRD: '3D368815665961',
};

const virtualSoftwareClientId = virtualSoftwareClientIds[env];

/* Auth0 Login URL */
function buildAuth0LoginUrl(audience) {
  const params = {
    client_id: auth0ClientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: 'openid profile email offline_access',
    state: encodeURIComponent(JSON.stringify({ t: Date.now() })),
    audience,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  };

  const queryString = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');

  return `${auth0Domains[env]}/authorize?${queryString}`;
}

export function getAuth0LoginUrl() {
  return buildAuth0LoginUrl(auth0CXCAudience);
}

export const auth0TokenApiParams = {
  url: `${authTokenEndpoint}`,
  data: {
    grant_type: 'authorization_code',
    client_id: auth0ClientId,
    code_verifier: codeVerifier,
    redirect_uri: redirectUri,
  },
};

/* Auth0 Logout URL */
const returnTo = `${redirectUri}`;
const auth0LogoutUrlParams = {
  url: `${auth0Domains[env]}/v2/logout?`,
  query: `returnTo=${encodeURIComponent(returnTo)}`,
};

export const auth0LogoutUrl =
  auth0LogoutUrlParams.url + auth0LogoutUrlParams.query;

/**
 * All CIM user types (to be deprecated)
 */
export const userAccountType = {
  OnlineSupportProfessional: 'OnlineSupportProfessional',
  OnlineSupportDistributor: 'OnlineSupportDistributor',
  ContentProfessional: 'ContentProfessional',
  RegistrationProfessional: 'RegistrationProfessional',
  RegistrationDistributor: 'RegistrationDistributor',
};

/**
 * Allowed CIM types for DPX (to be deprecatedq)
 */
export const allowedUserAccountTypes = [
  userAccountType.OnlineSupportProfessional,
  userAccountType.RegistrationProfessional,
  userAccountType.OnlineSupportDistributor,
  userAccountType.RegistrationDistributor,
  userAccountType.ContentProfessional,
];

/**
 * Roles
 */
export const ACCESS_ROLES = {
  RESOURCES_AND_TRAINING: 'Resources & Training',
  PATIENT_MANAGEMENT: 'Patient Management',
};

export const deviceType = {
  Implant: 'implant',
  Actuator: 'actuator',
  SoundProcessor: 'sound processor',
  SpeechProcessor: 'speech processor',
  RemoteAssistant: 'remote assistant',
  Accessory: 'accessory',
  ActuatorUnit: 'actuator unit',
  Other: 'other',
};

export const ASSET_STATUS = {
  Loaner: 'Loaner',
  ExchangeStock: 'Exchange Stock',
};

export const LOANER_STATUS = {
  LoanerWithClinic: 'LoanerWithClinic',
  LoanerInUse: 'LoanerInUse',
  LoanerInUseWithoutSR: 'LoanerInUseWithoutSR',
  LoanerReturnRecommended: 'LoanerReturnRecommended',
  LoanerReturnCritical: 'LoanerReturnCritical',
};

/**
 * Routes
 */
export const routes = {
  customerService:
    'https://www.cochlear.com/global/en/customer-service',
  dpx: {
    home: 'home',
    profile: 'profile',
    selectClinics: 'select-clinic',
    authorizer: '/authorize',
    unauthorized: '/unauthorized',
    initializer: '/initialize',
    patient: 'patients',
    advancedSearch: 'advanced-search',
    locatePatient: 'locate-patient',
    resources: 'resources',
    error: 'error',
    patientDetails: 'patients/details',
    editPatient: 'patients/edit',
    addCarer: 'patients/add-carer',
    createPatientLanding: 'create-patient-landing',
    createPatientRecipient: 'create-patient-recipient',
    createPatientCarer: 'create-patient-carer',
    createPatient: 'create-patient?condition=recipient',
    deviceRegistration: 'device-registration',
    deviceRegistrationReview: 'device-registration-review',
    deviceRegistrationSuccess: 'device-registration-success',
    accessoryRegistrationReview: 'accessory-registration-review',
    logout: '/authorize?action=logout',
    onboardingQRCode: 'onboarding-qr-code',
    teamMembers: 'team-members',
    clinicEquipment: 'clinic-equipment',
    assignLoaner: 'assign-loaner',
    assignLoanerDetails: 'assign-loaner-details',
    reportLoanerIssue: 'report-loaner-issue',
    createServiceRequest: 'create-service-request',
    viewServiceRequest: 'view-service-request',
  },
  mcp: {
    domain: `${mcpDomains[env]}`,
    home: `${mcpDomains[env]}/wps/OnlineServices/manageRecipient/onlineServicesDashboard`,
    warmup: `${mcpDomains[env]}/wps/OnlineServices/manageRecipient/onlineServicesDashboard?dpx=true&init=true`,
    clinic: `${mcpDomains[env]}/wps/OnlineServices/manageRecipient/context/CHANGE_ACTIVE_CLINIC?clinicId=`,
    servieRequest: `${mcpDomains[env]}/wps/OnlineServices/manageRecipient/recipientOverview/SET_SELECTED_RECIPIENT_CID?dpx=true&selectedRecipientCId=`,
    viewServiceRequestWithoutDomain: `/wps/OnlineServices/manageRecipient/viewServiceRequest/VIEW_SERVICE_REQUEST_URL?selectedServiceRequestId=##___SERVICEREQUESTID___##&selectedRecipientId=`,
    createServiceRequest: `${mcpDomains[env]}/wps/OnlineServices/manageRecipient/serviceRequest/SHOW_NEW_SERVICE_REQUEST_DPX?dpx=true&clinic_cochlear_id=##___CLINICCOCHLEARID___##&recipient_cochlear_id=##___RECIPIENTCOCHLEARID___##&instance_id=`,
    programmingFileRequests: `${mcpDomains[env]}/wps/OnlineServices/manageRecipient/context/CHANGE_ACTIVE_CLINIC_DPX?dpx=true&clinicCochlearId=`,
    recipientAdditionalData: `${mcpDomains[env]}/wps/OnlineServices/manageRecipient/recipientOverview/RECIPIENT_ADDITIONAL_DATA?selectedRecipientCId=`,
    account: `${mcpDomains[env]}/wps/OnlineServices/myAccount?dpx=true&expand=`,
    logout: `${mcpDomains[env]}/wps/OnlineServices/myAccount/LOGOUT?from=dpx`,
    clinicManagement: `${mcpDomains[env]}/wps/OnlineServices/manageClinic/clinicManagement/SHOW_CLINIC_MGMT?dpx=true`,
    clinicEquipment: `${mcpDomains[env]}/wps/OnlineServices/manageRecipient/loaner/VIEW_MANAGE_LOANERS?menuItemId=%23mnu-assign-loaner&dpx=true`,
    cspCamTraining: `${mcpDomains[env]}/wps/OnlineServices/resources/[REGION]/[LANG]/custom-sound/custom-sound-pro#/`,
    cspVirtualSoftware: `${auth0Domains[env]}/samlp/${virtualSoftwareId}?RelayState=https%3A%2F%2Fappstream2.ap-southeast-2.aws.amazon.com%2Fsaml%3Fstack%3Dcsp-stack%26accountId%${virtualSoftwareClientId}%26app%3DCustomSound`,
    cpnResource: `${mcpDomains[env]}/wps/OnlineServices/resources/mcp-cam/en/CPN_Professionals?dpx=true`,
    resources: `${mcpDomains[env]}/wps/OnlineServices/resources/[REGION]/en/resources?dpx=true`,
    resourcesHome: `${mcpDomains[env]}/wps/OnlineServices/resources/[REGION]/en/resources`,
    keepAlive: `${mcpDomains[env]}/wps/OnlineServices/dpx-session.jsp`,
  },
  clinicianPortal: {
    remoteCheck: `${clinicianPortalDomains[env]}/remoteCheck?theme=dpx&cId=`,
    home: `${clinicianPortalDomains[env]}/`,
  },
  auth0: {
    domain: auth0Domains[env],
  },
  expApi: {
    portalConfig: `${providerListEndpoint}/config/portal`,
  },
  remoteCheckScriptPath: `${clinicianPortalDomains[env]}/enrolmentStatusJS/latest`,
  scheduleRemoteCheckScriptPath: `${clinicianPortalDomains[env]}/public/latest/remoteModules/remote_check.js`,
};

/**
 * i18 cache
 */
export const i18CachePrefix = 'i18next_mcp_res_';
export const i18CacheExpiryTime = 5 * 60 * 1000; // 5 minutes

/**
 * Token Renewal
 */
export const tokenRenewThresholdInSecs = 10 * 60; // 10 minutes
export const tokenRenewCheckIntervalInSecs = 3 * 60; // 3 minutes

/**
 * Idle Timer
 */
export const idleTimerTimeout = 50 * 60 * 1000; // 50 minutes
export const idleTimerPromptBeforeIdle = 5 * 60 * 1000; // 5 minutes

/**
 * Provider Details object in storage Time to Live
 */
export const cpdTtl = 90 * 24 * 60 * 60 * 1000; // 90 days

/**
 * GTM Ids
 */
const gtmIds = {
  OFFLINE: 'GTM-T9LTVS8J',
  LOCAL: 'GTM-T9LTVS8J',
  DEV: 'GTM-T9LTVS8J',
  SIT: 'GTM-T9LTVS8J',
  UAT: 'GTM-T9LTVS8J',
  PRD: 'GTM-T9LTVS8J',
};

export const gtmId = gtmIds[env];

export const defaultBrowserLanguage = 'en';

export const supportedLanguagesLoginFlow = [
  'en-US',
  'en-GB',
  'de-DE',
  'fr-FR',
  'it-IT',
  'es-ES',
  'sv-SE',
  'nl-NL',
  'tr-TR',
  'pt-BR',
  'ja-JP',
  'ko-KR',
  'zh-CN',
  'zh-HK',
];

export const supportedLanguages = [
  'en-US',
  'en-GB',
  'de-DE',
  'fr-CA',
  'fr-FR',
  'it-IT',
  'sv-SE',
  'nl-NL',
  'tr-TR',
  'ja-JP',
  'ko-KR',
  'zh-CN',
  'zh-TW',
];

export const showReturnToLegacyPortal = false;
export const timestampExpired = 1726099200000; // Expired timestamp
export const defaultMinimumDate = '1983-06-08'; // Default minimum date for device registration

// Sentry configuration
const sentryConfig = {
  OFFLINE: {
    dsn: 'https://34adf6b34a26d549b56476a88875f11c@sentry.shared.cochlear.link/62',
    environment: 'offline',
    apiEndpoint: envUrl,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  },
  LOCAL: {
    dsn: 'https://34adf6b34a26d549b56476a88875f11c@sentry.shared.cochlear.link/62',
    environment: 'local',
    apiEndpoint: providerListEndpoint,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  },
  DEV: {
    dsn: 'https://34adf6b34a26d549b56476a88875f11c@sentry.shared.cochlear.link/62',
    environment: 'development',
    apiEndpoint: providerListEndpoint,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  },
  SIT: {
    dsn: 'https://34adf6b34a26d549b56476a88875f11c@sentry.shared.cochlear.link/62',
    environment: 'sit',
    apiEndpoint: providerListEndpoint,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  },
  UAT: {
    dsn: 'https://34adf6b34a26d549b56476a88875f11c@sentry.shared.cochlear.link/62',
    environment: 'uat',
    apiEndpoint: providerListEndpoint,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  },
  PRD: {
    dsn: 'https://fb0e09f91961c9ebb6226be2707389f6@sentry.cochlear.dev//106',
    environment: 'production',
    apiEndpoint: providerListEndpoint,
    tracesSampleRate: 0.25,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  },
};

export const getSentryConfig = () => {
  return sentryConfig[env] || sentryConfig.OFFLINE;
};
